import { ChevronDownIcon, ExternalLinkIcon, LinkIcon } from "@chakra-ui/icons";
import {
  Flex,
  chakra,
  Box,
  Container,
  Grid,
  ButtonGroup,
  Image,
  Text,
  Heading,
  Button,
  useToken,
  Icon,
  Spacer,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaShare } from "react-icons/fa";
import { InstantSearch, useHits } from "react-instantsearch-hooks";
import ExpandableSection from "@/components/ExpandableSection";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import PageInfo from "@/components/PageInfo";
import {
  CollapsibleFilter,
  FilterSection,
  RefinementList,
  Toggle,
  SearchBox,
  Pagination,
  SearchableRefinementList,
} from "@/components/SearchTools";
import { LinkButton } from "@/components/shared/generic";
import ModalAuth, { ScrollAuthWall } from "@/components/user/ModalAuth";
import { withUserData } from "@/contexts/UserDataContext";
import searchClient from "@/utils/algolia-init";
import { getRandomColor } from "@/utils/string";

const Hero = () => (
  <Flex
    direction="column"
    pos="relative"
    w="100vw"
    h={{ base: "90vh", md: "100vh" }}
    bg="linear-gradient(to top left, #c4a114, #85100c)"
  >
    <chakra.div flex="1">
      <Navbar inverted />
    </chakra.div>
    <Container textAlign="center">
      <chakra.h1
        color="white"
        textStyle="pageHeader"
        lineHeight="1.25"
        fontSize={{ base: "5xl", md: "8xl" }}
      >
        200+ Merit Scholarships
      </chakra.h1>
      <chakra.h2 color="white" mt={{ base: 20, md: 32 }} fontSize={{ base: "xl", md: "3xl" }}>
        Find the perfect scholarship for you.
      </chakra.h2>
      <Image
        alt="Splash"
        src="/images/search/splash.png"
        h="15vh"
        mb={-4}
        draggable={false}
        display="inline-block"
      />
    </Container>
  </Flex>
);

const FilterArea = () => (
  <Grid
    templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }}
    overflow="visible"
    gap={4}
    pl={{ base: 2, md: 0 }}
    mt={4}
  >
    <CollapsibleFilter header="Selectivity" color="yellow.700">
      <RefinementList attribute="selectivity" />
    </CollapsibleFilter>
    <CollapsibleFilter header="What's Covered" color="orange.700">
      <RefinementList attribute="offered" />
    </CollapsibleFilter>
    <CollapsibleFilter header="University" color="red.700">
      <SearchableRefinementList selectVariant="flushed" attribute="university" limit={1000} />
    </CollapsibleFilter>
  </Grid>
);

const Hit = ({ offered, selectivity, name, university, url }) => {
  const [color, setColor] = useState("black");
  const darkColor = useToken("colors", color + ".500");

  useEffect(() => {
    const colors = ["black", "red", "orange", "yellow"];
    setColor(colors[Math.floor(Math.random() * colors.length)]);
  }, []);

  return (
    <Flex
      direction="column"
      p={4}
      bg="white"
      borderRadius="lg"
      borderTop={`1px solid ${darkColor}`}
      borderRight={`1px solid ${darkColor}`}
      borderBottom={`0.25rem solid ${darkColor}`}
      borderLeft={`0.25rem solid ${darkColor}`}
    >
      <Heading as="h3" fontSize={{ base: "lg", md: "2xl" }}>
        {university}
      </Heading>
      <Text mb={4} fontSize="lg" fontWeight="bold">
        {name}
      </Text>

      {offered && (
        <Text>
          <chakra.b>What&apos;s offered:&nbsp;</chakra.b>
          {offered.join(", ")}
        </Text>
      )}
      {selectivity && (
        <Text>
          <chakra.b>Selectivity:&nbsp;</chakra.b>
          {selectivity}
        </Text>
      )}
      <Spacer />
      {url && (
        <LinkButton
          target="_blank"
          colorScheme={color}
          href={"https://google.com/search?q=" + encodeURIComponent(url)}
          mt={2}
          leftIcon={<ExternalLinkIcon />}
        >
          Learn More
        </LinkButton>
      )}
    </Flex>
  );
};

const Hits = () => {
  const { hits } = useHits();
  if (hits.length === 0) {
    return (
      <Box textAlign="center" mt={8}>
        <Heading as="h3" fontSize={{ base: "lg", md: "2xl" }}>
          No results found.
        </Heading>
        <Text mt={4} fontSize="lg">
          Try searching for something else.
        </Text>
      </Box>
    );
  }
  return (
    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
      {hits.map((hit, i) => (
        <Hit key={hit.objectID} {...hit} />
      ))}
    </Grid>
  );
};

const Scholarships = () => {
  return (
    <>
      <PageInfo
        overrideOGImage="https://admityogi.com/images/scholarships/og.png"
        title="200+ Merit Scholarships for High School Students"
        description="Find the perfect scholarship for you."
      />
      <ScrollAuthWall base={1000} md={1300} />
      <Hero />
      <InstantSearch searchClient={searchClient} indexName="scholarship_list">
        <Container maxW="container.lg" id="results" my={8}>
          <Heading as="h2" fontSize={{ base: "xl", md: "3xl" }}>
            Our Mission
          </Heading>
          <Text mt={4} mb={4} fontSize="xl">
            We&apos;re on a mission to help students find the perfect scholarship for them.
            We&apos;ve compiled a list of over 200 merit scholarships for high school students, and
            we&apos;re adding more every day.
          </Text>
          <FilterSection>
            <FilterArea />
          </FilterSection>
        </Container>
        <Container maxW="container.lg" pb={10}>
          <Pagination showNResults={false} />
          <Hits />
        </Container>
      </InstantSearch>
      <Footer />
    </>
  );
};

export default withUserData(Scholarships);
